import React, { useCallback, useRef, useState } from 'react';
import { X } from 'lucide-react';
import CriteriaGroupCard from './CriteriaGroupCard';
import { CriteriaDragProvider } from './CriteriaDragContext';
import {
  appendChildAtPath,
  defaultCriteriaRoot,
  duplicateAtPath,
  getNode,
  groupNodesAtPath,
  moveNodeToPath,
  moveOutOfGroupAtPath,
  moveWithinParent,
  removeAtPath,
  reshuffleSampleAtPath,
  samplePctLostByRemoving,
  setSamplePctAtPath,
  updateNodeAtPath,
  wrapInGroupAtPath,
} from './criteriaUtils';

/**
 * Root of the criteria editor. Owns tree mutations; rendering is delegated to group/condition components.
 * `onChange` must accept a functional updater (React state setter) so same-tick edits compose.
 */
const CriteriaTree = ({ criteria, onChange, groupCounts, countsStale = false }) => {
  const root = criteria || defaultCriteriaRoot();
  // Path key of the condition added most recently, so its column picker opens automatically once.
  const newPathsRef = useRef(new Set());
  // Percentage discarded by the last move out of a group, shown until dismissed.
  const [droppedSamplePct, setDroppedSamplePct] = useState(null);

  const apply = (fn) => onChange((prev) => fn(prev || defaultCriteriaRoot()));

  const handleChange = (path, updater) => apply((prev) => updateNodeAtPath(prev, path, updater));
  const handleRemove = (path) => apply((prev) => removeAtPath(prev, path));
  const handleAppend = (path, child) => {
    if (child.kind === 'condition') {
      const parent = getNode(root, path);
      const idx = (parent?.children || []).length;
      newPathsRef.current = new Set([[...path, idx].join('-')]);
    }
    apply((prev) => appendChildAtPath(prev, path, child));
  };
  const handleDuplicate = (path) => apply((prev) => duplicateAtPath(prev, path));
  const handleSamplePctChange = (path, pct) => apply((prev) => setSamplePctAtPath(prev, path, pct));
  const handleReshuffleSample = (path) => apply((prev) => reshuffleSampleAtPath(prev, path));
  const handleWrap = (path) =>
    apply((prev) => {
      const parent = getNode(prev, path.slice(0, -1));
      return wrapInGroupAtPath(prev, path, parent?.op === 'OR' ? 'AND' : 'OR');
    });
  const handleMoveOut = (path) => {
    setDroppedSamplePct(samplePctLostByRemoving(root, path));
    apply((prev) => moveOutOfGroupAtPath(prev, path));
  };
  const handleReorder = (path, delta) => apply((prev) => moveWithinParent(prev, path, delta));

  // Dragging the last child out of a sampled group loses that percentage the same way
  // the move-out button does, so both report it.
  const handleDragMove = useCallback(
    (fromPath, toParentPath, index) => {
      onChange((prev) => {
        const base = prev || defaultCriteriaRoot();
        setDroppedSamplePct(samplePctLostByRemoving(base, fromPath));
        return moveNodeToPath(base, fromPath, toParentPath, index);
      });
    },
    [onChange]
  );

  const handleDragGroup = useCallback(
    (targetPath, draggedPath) => {
      onChange((prev) => {
        const base = prev || defaultCriteriaRoot();
        setDroppedSamplePct(samplePctLostByRemoving(base, draggedPath));
        return groupNodesAtPath(base, targetPath, draggedPath);
      });
    },
    [onChange]
  );

  return (
    <CriteriaDragProvider onMove={handleDragMove} onGroup={handleDragGroup}>
      {droppedSamplePct != null ? (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
          <span className="flex-1">
            That group only held one condition, so moving it out removed the group and its{' '}
            <span className="font-semibold">{droppedSamplePct}%</span> sample. This branch now matches everyone again —
            expect the count to grow.
          </span>
          <button
            type="button"
            onClick={() => setDroppedSamplePct(null)}
            className="rounded p-0.5 text-amber-600 hover:bg-amber-100 hover:text-amber-900"
            title="Dismiss"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}
      <CriteriaGroupCard
        node={root}
        path={[]}
        onChange={handleChange}
        onRemove={handleRemove}
        onAppend={handleAppend}
        onDuplicate={handleDuplicate}
        onWrap={handleWrap}
        onSamplePctChange={handleSamplePctChange}
        onReshuffleSample={handleReshuffleSample}
        onMoveOut={handleMoveOut}
        onReorder={handleReorder}
        newPaths={newPathsRef.current}
        groupCounts={groupCounts}
        countsStale={countsStale}
      />
    </CriteriaDragProvider>
  );
};

export default CriteriaTree;
